import { memo } from "react";

import "./InlineChart.css";

// Gráfica interactiva inline del chat (camino web de create_chart):
// el bot manda el config de Chart.js en channelData.chartSpec y aquí
// se renderiza dentro de un iframe sandboxed. El config lo produce un
// LLM, así que se trata como contenido no confiable: mismo
// aislamiento que el panel de artifacts (sandbox sin
// allow-same-origin + CSP sin red). En Teams este componente no
// existe: allá la gráfica sigue siendo un PNG adjunto.

// CSP inyectada en el documento del iframe. DEBE mantenerse en
// sincronía con ArtifactPanel.tsx y con las reglas del prompt
// CHART_GENERATOR_JS del backend (src/agentic/prompts.py).
const CHART_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline' https://cdn.jsdelivr.net",
  "style-src 'unsafe-inline' https://cdn.jsdelivr.net",
  "img-src data: blob:",
  "font-src data: https://cdn.jsdelivr.net",
  "connect-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
].join("; ");

// Misma URL de Chart.js que permite la CSP de los artifacts.
const CHART_JS_SRC = "https://cdn.jsdelivr.net/npm/chart.js";

// Extensiones de Chart.js que el config (JSON puro, sin funciones) sólo
// puede ACTIVAR por nombre. Hoy las activan los puentes (waterfall) que
// normaliza force_horizontal_waterfall en el backend
// (src/agentic/tools/chart_tool.py): los nombres DEBEN coincidir con
// CURSOR_TOOLTIP_POSITION y BAR_VALUES_PLUGIN de allá. Un config que no
// las pide se dibuja igual que antes.
// - Posición "cursor": el tooltip sale donde está el cursor, no en la
//   punta de la barra. Chart.js deja el tooltip activo cuando el cursor
//   sale del área de las barras (ejes, título) y, con esa posición, lo
//   seguía por ahí: "lucaTooltipOffBar" lo apaga en cuanto el cursor no
//   está sobre una barra.
// - Plugin "lucaBarValues": el valor de cada barra horizontal a su
//   derecha, a 6 px. En una barra que sale de 0 (un total) es su valor;
//   en una flotante [inicio, fin] (un impacto), fin - inicio con signo.
//   Con los decimales que traen los datos (máximo 2).
const CHART_EXTENSIONS = `
    var tooltipPlugin = Chart.Tooltip || Chart.registry.getPlugin("tooltip");
    tooltipPlugin.positioners.cursor = function (elements, eventPosition) {
      return eventPosition;
    };
    Chart.register({
      id: "lucaTooltipOffBar",
      afterEvent: function (chart, args) {
        var tip = chart.tooltip;
        if (!tip || chart.options.plugins.tooltip.position !== "cursor") return;
        if (!tip.getActiveElements().length) return;
        var onBar = chart.getElementsAtEventForMode(
          args.event, "nearest", { intersect: true }, false);
        if (!onBar.length) {
          tip.setActiveElements([], { x: args.event.x, y: args.event.y });
          args.changed = true;
        }
      }
    });
    Chart.register({
      id: "lucaBarValues",
      defaults: { display: false },
      afterDatasetsDraw: function (chart, args, opts) {
        if (!opts.display || chart.options.indexAxis !== "y") return;
        var ctx = chart.ctx;
        ctx.save();
        ctx.font = Chart.helpers.toFont(Chart.defaults.font).string;
        ctx.fillStyle = "#3d3d3d";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        chart.data.datasets.forEach(function (dataset, di) {
          if (!chart.isDatasetVisible(di)) return;
          var points = dataset.data || [];
          var decimals = 0;
          points.forEach(function (p) {
            [].concat(p).forEach(function (v) {
              var s = String(v), dot = s.indexOf(".");
              if (dot !== -1) decimals = Math.max(decimals, s.length - dot - 1);
            });
          });
          decimals = Math.min(decimals, 2);
          var format = function (n) {
            return n.toLocaleString("es-MX", {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals
            });
          };
          chart.getDatasetMeta(di).data.forEach(function (bar, i) {
            var p = points[i];
            var floating = Array.isArray(p);
            var start = floating ? Number(p[0]) : 0;
            var end = floating ? Number(p[1]) : Number(p);
            if (!isFinite(start) || !isFinite(end)) return;
            var text;
            if (start === 0) {
              text = format(end);
            } else {
              var delta = end - start;
              text = (delta > 0 ? "+" : delta < 0 ? "-" : "") +
                format(Math.abs(delta));
            }
            // Posición animada: la etiqueta acompaña a la barra mientras
            // crece al cargar.
            var pos = bar.getProps(["x", "y", "base"]);
            ctx.fillText(text, Math.max(pos.x, pos.base) + 6, pos.y);
          });
        });
        ctx.restore();
      }
    });
`;

// El config viaja como JSON embebido en un <script> inline: se
// escapan < y > para que un "</script>" dentro de un label no pueda
// cerrar el tag (el JSON escapado sigue siendo JSON válido).
const wrapChartHtml = (spec: any) => {
  const specJson = JSON.stringify(spec)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e");
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CHART_CSP}">
<style>
  html, body { margin: 0; padding: 8px; background: #fff; height: 100%;
    box-sizing: border-box; font-family: "Segoe UI", Arial, sans-serif; }
  *, *::before, *::after { box-sizing: inherit; }
  .chart-wrap { position: relative; height: calc(100vh - 16px); }
  .chart-error { color: #808080; font-size: 13px; }
</style>
</head>
<body>
<div class="chart-wrap"><canvas id="luca-chart"></canvas></div>
<script src="${CHART_JS_SRC}"></script>
<script>
  try {
${CHART_EXTENSIONS}
    var config = ${specJson};
    config.options = config.options || {};
    config.options.responsive = true;
    config.options.maintainAspectRatio = false;
    new Chart(document.getElementById("luca-chart"), config);
  } catch (err) {
    document.body.innerHTML =
      '<p class="chart-error">The chart could not be rendered.</p>';
  }
</script>
</body>
</html>`;
};

function InlineChart({ spec }: { spec: any }) {
  if (!spec || typeof spec !== "object") return null;
  return (
    <div className="inline-chart">
      <iframe
        className="inline-chart-frame"
        title="Luca chart"
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        srcDoc={wrapChartHtml(spec)}
      />
    </div>
  );
}

export default memo(InlineChart);
